/* eslint no-loops/no-loops: 0, no-plusplus: 0 */

import moment from "moment";
import OkC from "shared/util/OkcApi";
import isApp from "shared/util/isApp";
import Cookies from "shared/util/Cookies";
import _str from "shared/util/_str";
import _strf from "shared/util/_strf";
import Context from "shared/util/Context";
import { COUNTRIES_BY_CODE } from "shared/dictionaries/countries";
import { GENTATION } from "shared/dictionaries/gentation";

import {
	MULTISELECT_FIELDS,
	FIELDS_MAP,
	HEIGHT_RANGE,
	ERROR,
	UNITED_STATES,
	INDIA,
	EMOJI_REGEX,
	UNDERAGE_STATUS,
	FACEBOOK_UNDERAGE_STATUS,
} from "apps/profile_details/util/constants";

export function getNestedValue(object, path) {
	return (
		path.split(".").reduce(
			(value, element) => {
				if (value && value[element]) {
					return value[element];
				} else {
					return null;
				}
			},
			object,
		)
	);
}

export function makeMetricHeightRange(min, max) {
	const heightRange = [];

	// placeholder for null value.
	heightRange.push({ value: "", name: "Height (m/cm)", disabled: true });

	for (let i = min; i <= max; i++) {
		// use decimillimeters as value
		heightRange.push({ value: i * 100, name: `${i} cm` });
	}

	return heightRange;
}

// api value is rounded
function _inchesToDecimillimeters(inches) {
	return Math.floor(inches * 2.54) * 100;
}

export function makeImperialHeightRange(min, max) {
	const heightRange = [];

	// placeholder for null value.
	heightRange.push({ value: "", name: _str("Height (ft/in)"), disabled: true });

	let heightValue = min * 12; // in inches

	for (let feet = min; feet <= max; feet++) {
		for (let inch = 0; inch <= 11; inch++) {
			// use decimillimeters as value
			heightRange.push({
				value: _inchesToDecimillimeters(heightValue),
				name: `${feet}’ ${inch}”`,
			});
			heightValue += 1;
		}
	}

	return heightRange;
}

export function _findObjectIndex(array, property, targetValue) {
	const arrayLength = array.length;

	for (let i = 0; i < arrayLength; i++) {
		if (array[i][property] === targetValue) {
			return i;
		}
	}

	return -1;
}

export function makeCountryList() {
	const countries = [];

	Object.keys(COUNTRIES_BY_CODE).forEach((country) => {
		countries.push({
			value: COUNTRIES_BY_CODE[country].name,
			name: COUNTRIES_BY_CODE[country].name,
		});
	});

	return countries;
}

export function makeGenderList(genders) {
	const gendersCopy = [...genders];
	const genderList = [];

	// make sure man is the first value and woman is the second
	const indexOfMan = _findObjectIndex(genders, "name", "Man");
	const indexOfWoman = _findObjectIndex(genders, "name", "Woman");

	if (indexOfMan !== -1 && indexOfWoman !== -1) {
		genderList.push(
			{ value: genders[indexOfWoman].id, name: genders[indexOfWoman].name },
			{ value: genders[indexOfMan].id, name: genders[indexOfMan].name },
		);

		gendersCopy.splice(indexOfMan, 1);
		gendersCopy.splice(indexOfWoman, 1);
	}

	const gendersCount = gendersCopy.length;

	/* eslint-disable no-loops/no-loops */

	for (let i = 0; i < gendersCount; i++) {
		genderList.push({ value: gendersCopy[i].id, name: gendersCopy[i].name });
	}

	/* eslint-disable no-loops/no-loops */

	return genderList;
}

/**
 * Set a default of 18 years ago for a user's birthday if we get a default date
 * from the profile details endpoint (this happens for new accounts without
 * birthdays).
 *
 * @param {object} _data - an object with the keys year, month, and day
 * @return {object} - same thing
 */
const prepBirthday = (_data) => {
	const data = { ..._data };

	if (typeof data !== "object" || !data.year || !data.month || !data.day) {
		return data;
	}

	// check for the garbage defaults from the endpoint
	if (data.year === 1 && data.month === 1 && data.day === 1) {
		data.year = null;
		data.month = null;
		data.day = null;
	}

	return data;
};

// parse the api data to set up the store for each field
export function parseApiData(apiData, userFieldsData, genderOptions, genderMapping, path = "") {
	// set up the store for each field
	let fields = {};

	Object.keys(apiData).forEach((field) => {
		if (Array.isArray(apiData[field])) {
			// if there is a provided path, keep track of the path and store it as the key
			// ex: religion has 2 nested objects within it, modifier & value
			// so the pathNames are religion.modifier and religion.value, respectively
			const pathName = path ? `${path}.${field}` : field;
			let dataKey = pathName;

			// the key to get ethnicity values is under `ethnicities`,
			// but the key to retrieve user ethnicity is `ethnicity`
			if (field === FIELDS_MAP.ETHNICITIES) {
				dataKey = "ethnicity";
			}

			// if the user has filled out this field already, use it
			let userFieldData = getNestedValue(userFieldsData, dataKey) || "";

			// transform all internal data to use empty string instead of &mdash;
			if (userFieldData === "&mdash;") {
				userFieldData = "";
			}

			const isMultiselect = MULTISELECT_FIELDS.indexOf(pathName) !== -1;

			if (isMultiselect) {
				if (!userFieldData || userFieldData.indexOf("&mdash;") > -1) {
					userFieldData = [];
				} else if (!Array.isArray(userFieldData)) {
					userFieldData = [userFieldData];
				}
			}

			fields[pathName] = {
				name: pathName,
				options: apiData[field],
				isSkipped: false,
				isValid: null,
				isMultiselect,
				data: userFieldData,
			};
		} else if (apiData[field] !== null && typeof (apiData[field] === "object")) {
			// if the field is an object (ex: religion has a modifier and value key),
			// go through each key and parse
			const nestedFieldResult = parseApiData(
				apiData[field], userFieldsData, genderOptions, genderMapping, field,
			);

			fields = {
				...fields,
				...nestedFieldResult,
			};
		}
	});


	// manual additions
	// --------------------------------------------------------------------------

	fields[FIELDS_MAP.NAME] = {
		name: FIELDS_MAP.NAME,
		options: [],
		isSkipped: false,
		isValid: null,
		isMultiselect: false,
		data: userFieldsData.name,
	};

	fields[FIELDS_MAP.HEIGHT] = {
		name: FIELDS_MAP.HEIGHT,
		options: userFieldsData.metric === 1 ? HEIGHT_RANGE.METRIC : HEIGHT_RANGE.IMPERIAL,
		isSkipped: false,
		isValid: null,
		isMultiselect: false,
		data: userFieldsData[FIELDS_MAP.HEIGHT] || "",
	};

	fields[FIELDS_MAP.BIRTHDATE] = {
		name: FIELDS_MAP.BIRTHDATE,
		options: [],
		isSkipped: false,
		isValid: null,
		isMultiselect: false,
		data: prepBirthday(userFieldsData.birthdate),
	};

	const defaultCountry = Context.has("IS_INDIAN_USER") ? INDIA : UNITED_STATES;

	fields[FIELDS_MAP.LOCATION] = {
		name: FIELDS_MAP.LOCATION,
		options: makeCountryList(),
		isSkipped: false,
		isValid: null,
		isMultiselect: false,
		data: userFieldsData[FIELDS_MAP.LOCATION] || { country: defaultCountry },
	};

	fields[FIELDS_MAP.GENDER_TAGS] = {
		name: FIELDS_MAP.GENDER_TAGS,
		options: makeGenderList(genderOptions),
		isSkipped: false,
		isValid: null,
		isMultiselect: true,
		data: userFieldsData.gender_tags || [],
	};

	fields[FIELDS_MAP.UNDERLYING_GENDER] = {
		name: FIELDS_MAP.UNDERLYING_GENDER,
		options: makeGenderList(genderMapping),
		isSkipped: false,
		isValid: null,
		isMultiselect: false,
		data: userFieldsData.gender && userFieldsData.gender !== "unknown" ? userFieldsData.gender : "",
	};

	return fields;
}

export function formatApiData(data) {
	// unflatten an object from { "very.nested.object": "hi" }
	// to { very: { nested: { object: "hi" }}}
	const callData = {};
	const dataCopy = { ...data };

	Object.keys(data).forEach((dataKey) => {
		// since the api expects `"&mdash;"`, revert all unanswered fields to `"&mdash;"`
		if (data[dataKey] === "") {
			if (dataKey === FIELDS_MAP.HEIGHT) {
				// don't save height if they didn't fill it out
				return;
			} else {
				dataCopy[dataKey] = "&mdash;";
			}
		}

		// clean up location data
		if (dataKey === FIELDS_MAP.LOCATION) {
			if (dataCopy[dataKey].zip) {
				callData.postal_code = dataCopy[dataKey].zip;
			} else {
				callData.location = dataCopy[dataKey].locid;
			}

			callData.locid = dataCopy[dataKey].locid;
		}

		if (dataKey === FIELDS_MAP.UNDERLYING_GENDER) {
			// convert it to gender tag id
			if (dataCopy[dataKey] !== "&mdash;") {
				callData.gender = GENTATION.GENDER_ALIASES[dataCopy[dataKey]];
			}
		} else if (dataKey === FIELDS_MAP.GENDER_TAGS) {
			callData.gender_tags = dataCopy[dataKey];

			if (dataCopy[dataKey].length === 1 && GENTATION.GENDER_ALIASES[dataCopy[dataKey][0]]) {
				callData.gender = GENTATION.GENDER_ALIASES[dataCopy[dataKey][0]];
			}

			// sneak in status call here
			callData.status = "single";
		}

		const subKeys = dataKey.split(".");
		const lastSubKey = subKeys.pop();
		let key;

		// the key to get ethnicity values is under `ethnicities`,
		// but the key to save user ethnicity is `ethnicity`
		if (lastSubKey === FIELDS_MAP.ETHNICITIES) {
			key = "ethnicity";
		} else {
			key = lastSubKey;
		}

		if (key !== FIELDS_MAP.LOCATION && subKeys[0] !== "gender") {
			subKeys.reduce((result, subKey) => {
				/* eslint-disable no-param-reassign */
				result[subKey] = result[subKey] || {};

				/* eslint-enable no-param-reassign */
				return result[subKey];
			}, callData)[key] = dataCopy[dataKey];
		}
	});

	return callData;
}

export function toTitleCase(string) {
	return string.toLowerCase().split("").map((letter, idx) => {
		return idx === 0 ? letter[0].toUpperCase() : letter;
	}).join("");
}

export function getObjectIndex(array, property, targetValue) {
	const arrayLength = array.length;

	for (let i = 0; i < arrayLength; i++) {
		if (array[i][property] === targetValue) {
			return i;
		}
	}

	return -1;
}

export function getComment(field, input = false) {
	let comment = null;

	if (!input) {
		return comment;
	}

	switch (field) {
		default: {
			comment = null;

			return comment;
		}
	}
}

export function validate(field, data, pass, fail, suggest) {
	switch (field) {
		case FIELDS_MAP.BIRTHDATE: {
			const isIncomplete = ["year", "month", "day"].reduce((prev, key) => {
				return !data[key] || Number.isNaN(data[key]) || prev;
			}, false);

			if (isIncomplete) {
				return fail(ERROR[FIELDS_MAP.BIRTHDATE].MISSING);
			}

			const today = new Date();
			const birthdayThisYear = new Date(`${today.getFullYear()}/${data.month}/${data.day}`);
			const hasHadBirthdayThisYear = today >= birthdayThisYear;

			let age = today.getFullYear() - data.year;

			if (!hasHadBirthdayThisYear) {
				age -= 1;
			}

			if (age > 99) {
				return fail(ERROR[FIELDS_MAP.BIRTHDATE].INVALID);
			}

			return pass();
		}

		case FIELDS_MAP.LOCATION: {
			const {
				country, city, zip, longitude, latitude, locid,
			} = data;
			const params = {};

			if ((!country && !(city || zip)) && !(longitude && latitude)) {
				return fail(ERROR[FIELDS_MAP.LOCATION].REQUIRED);
			}

			if (longitude && latitude) {
				OkC.get("location/nearest", {
					data: {
						lat: latitude,
						lon: longitude,
					},
				})
					.then((res) => {
						const responseValues = {
							locid: res.locid,
							country: COUNTRIES_BY_CODE[res.country_code].name,
							state_code: res.state_code,
						};

						if (res.country_code === "US") {
							responseValues.zip = res.postal_code;
						} else {
							responseValues.city = res.city_name;
						}

						return pass(_strf("Ahh, $cityName.", {
							cityName: res.city_name,
						}), responseValues);
					})
					.catch(() => {
						return fail(_str("Sorry, we can’t locate you"));
					});

				return null;
			}

			// if we have a city and a locid, the user probably just made a selection
			// from the multiple city match dropdown. but regardless, we have enough
			// information to move along.
			if (locid && city) {
				return pass(
					_strf("Ahh, $cityName.", { cityName: city }),
					{ locid },
				);
			}

			// do basic zip validation and prepare the query. US users get "zip", and
			// non-US users get "city, country".
			if (country === UNITED_STATES && zip) {
				if (zip.length !== 5) {
					return fail(ERROR[FIELDS_MAP.LOCATION].ZIP_LENGTH);
				} else if (!/^\d+$/.test(zip)) {
					return fail(ERROR[FIELDS_MAP.LOCATION].ZIP_NUMBER);
				} else {
					params.q = `${zip}`;
				}
			} else if (country && city) {
				params.q = `${city}, ${country}`;
			}

			OkC.get("/location/query", {
				data: params,
			})
				.then((res) => {
					if (res && res.results && res.results.length) {
						if (res.results.length === 1) {
							return pass(res.message, {
								locid: res.results[0].locid,
								state_code: res.results[0].state_code,
							});
						} else {
							const suggestions = [];

							res.results.forEach((result) => {
								let cityName = result.city_name;

								if (result.state_name && result.state_name !== "") {
									cityName = `${cityName}, ${result.state_name}`;
								}

								suggestions.push({
									value: result.locid,
									name: cityName,
								});
							});

							return suggest(_str("Mulitple matches found"), suggestions);
						}
					} else {
						if (res && res.message) {
							return fail(res.message);
						}

						return fail(_str("Sorry, we can’t locate you"));
					}
				})
				.catch(() => {
					return fail(_str("Sorry, we can’t locate you"));
				});

			return null;
		}

		case FIELDS_MAP.NAME: {
			// basic name validation
			if (data.match(/\d/)) {
				return fail(_str("Sorry, your name can’t have numbers."));
			} else if (data === "") {
				return fail(_str("Your name can’t be blank!"));
			}

			OkC.get("/profile/name/check", {
				data: {
					name: data,
				},
			}).then(() => {
				return pass(_strf("$name. Has a nice ring to it.", {
					name: data,
				}));
			}).catch((err) => {
				return fail(
					err && err.error && err.error.message ?
						err.error.message :
						_str("Names can’t contain special characters."),
				);
			});

			return null;
		}

		case FIELDS_MAP.PRONOUNS: {
			// basic name validation
			if (data.match(/\d/)) {
				return fail(_str("Sorry, your pronouns can’t have numbers."));
			} else if (data.length >= 25) {
				return fail(_str("Sorry, your pronouns can't be longer than 25 characters."));
			} else if (data.match(EMOJI_REGEX)) {
				return fail(_str("Sorry, your pronouns can't contain emoji."));
			}
			else {
				return pass();
			}
		}

		// these should all be fine. i guess we could ensure
		// they're passing something in the list of options,
		// but it would require manual API fuckery and the backend
		// would catch that shit anyway
		case FIELDS_MAP.HEIGHT:
		case FIELDS_MAP.ETHNICITIES:
		case FIELDS_MAP.EDUCATION:
		case FIELDS_MAP.OCCUPATION:
		case FIELDS_MAP.RELIGION_VALUE:
		case FIELDS_MAP.CHILDREN_WANT:
		case FIELDS_MAP.DRINKING:
		case FIELDS_MAP.SMOKING:
		case FIELDS_MAP.DRUGS: {
			return pass();
		}

		default:
			return false;
	}
}

export function findAndHandleUnderageError(apiResults) {
	let isUnderage = false;

	if (!apiResults || !Array.isArray(apiResults)) {
		return;
	}

	// the results come back from the api in a bit of a nesty messy; we just want
	// to find the one needle for which we're looking...
	apiResults.forEach((result) => {
		if (result.res && Array.isArray(result.res)) {
			result.res.forEach((response) => {
				if (response.status && response.status === UNDERAGE_STATUS) {
					isUnderage = true;
				}
			});
		}
	});

	// If we found out they're underage via facebook, things are a little
	// bit simpler #nastymessy
	if (apiResults[0] === FACEBOOK_UNDERAGE_STATUS) {
		isUnderage = true;
	}

	// set a cookie (with at least a slightly cryptic name) and log out
	if (isUnderage) {
		Cookies.setCookie("verified_account", true, moment().add(10, "years").toDate());

		if (isApp()) {
			window.App.logout();
		} else {
			window.location.href = "/logout";
		}
	}

	// this has to return for the linter
	return;
}



// WEBPACK FOOTER //
// ./src/apps/profile_details/util/helpers.js